import { yupResolver } from '@hookform/resolvers/yup';
import { convertFromYesNo, convertToMaybeYesNoEnum } from '@zorro/common-utils';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { tryFormatDateISO, tryParseDateISO } from '@zorro/shared/formatters';
import { YesNo } from '@zorro/types';
import {
  DateInput,
  FormErrorMessage,
  Grid,
  InputWrapper,
  LabelWithTooltip,
} from '@zorro/web/design-system';
import {
  useForm,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { FormFooter } from '../FormFooter';
import {
  FinchSetupFormFields,
  earlyEnrollmentsOffsetOptions,
  finchSetupFormSchemaObject,
} from './FinchSyncSettingsFormUtils';

type Props = {
  onClose: () => void;
  employerId: string;
};

export const FinchSyncSettingsForm = ({ employerId, onClose }: Props) => {
  const { tryMutate: updateFinchEmployer } = useMonolithMutation({
    method: 'finchControllerUpdateEmployer',
  });
  const { data: finchEmployer, isLoading } = useMonolithQuery({
    method: 'finchControllerFindEmployer',
    params: [employerId],
  });

  const { control, getValues, formState } = useForm<FinchSetupFormFields>({
    mode: 'all',
    resolver: yupResolver(finchSetupFormSchemaObject),
    defaultValues: {
      earliestEnrollmentsSyncDate: tryParseDateISO(
        finchEmployer?.earliestEnrollmentsSyncDate
      )?.toDate(),
      earlyEnrollmentsOffset:
        finchEmployer?.earlyEnrollmentsOffset.toString() ?? '0',
      enforcePostTaxDeductions:
        convertToMaybeYesNoEnum(finchEmployer?.enforcePostTaxDeductions) ??
        YesNo.YES,
      isHsaDeductions: finchEmployer?.isHsaDeductions ?? false,
    },
  });

  const { errors, isValid, isDirty } = formState;

  const handleSubmit = async () => {
    const {
      earliestEnrollmentsSyncDate,
      earlyEnrollmentsOffset,
      enforcePostTaxDeductions,
      isHsaDeductions,
    } = getValues();

    const result = await updateFinchEmployer([
      employerId,
      {
        earliestEnrollmentsSyncDate: tryFormatDateISO(
          earliestEnrollmentsSyncDate
        ),
        earlyEnrollmentsOffset: parseInt(earlyEnrollmentsOffset),
        enforcePostTaxDeductions: convertFromYesNo(enforcePostTaxDeductions),
        isHsaDeductions,
      },
    ]);

    if (result.isOk()) {
      onClose();
    }
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: !isValid || !isDirty,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, isDirty, onClose]
  );
  useDrawerFooter(drawerFooter);

  if (!finchEmployer || isLoading) {
    return null;
  }

  return (
    <Grid>
      <Grid.Col>
        <Controller
          control={control}
          name="earliestEnrollmentsSyncDate"
          render={({ field }) => (
            <DateInput
              {...field}
              placeholder="Select date"
              label={
                <LabelWithTooltip
                  tooltipLabel="Establishes the historical data boundary. Zorro will not modify payroll runs prior to this date. This field must be defined before any deductions can be synced."
                  label="Deductions sync start date"
                />
              }
            />
          )}
        />
        <FormErrorMessage
          fieldName="earliestEnrollmentsSyncDate"
          errors={errors}
        />
      </Grid.Col>
      <Grid.Col>
        <Controller
          control={control}
          name="earlyEnrollmentsOffset"
          render={({ field: { ...rest } }) => (
            <Select
              {...rest}
              data={earlyEnrollmentsOffsetOptions}
              label={
                <LabelWithTooltip
                  tooltipLabel="Determines the write date relative to the pay period end date"
                  label="Write date offset"
                  isRequired
                />
              }
            />
          )}
        />
        <FormErrorMessage fieldName="earlyEnrollmentsOffset" errors={errors} />
      </Grid.Col>
      <Grid.Col>
        <Controller
          control={control}
          name="enforcePostTaxDeductions"
          render={({ field: { ...rest } }) => (
            <InputWrapper label="Deduction category preference" required>
              <TabButtonGroup {...rest} isFullWidth>
                <TabButtonGroup.List grow>
                  <TabButtonGroup.Tab value={YesNo.NO}>
                    All pre-tax
                  </TabButtonGroup.Tab>
                  <TabButtonGroup.Tab value={YesNo.YES}>
                    By plan market
                  </TabButtonGroup.Tab>
                </TabButtonGroup.List>
              </TabButtonGroup>
            </InputWrapper>
          )}
        />

        <FormErrorMessage
          fieldName="enforcePostTaxDeductions"
          errors={errors}
        />
      </Grid.Col>
      <Grid.Col>
        <Controller
          control={control}
          name="isHsaDeductions"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              label="Split deduction categories by HSA eligibility"
              checked={value}
              onChange={(event) => rest.onChange(event.target.checked)}
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );
};
